import React, { useContext, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { useAuth0 } from './react-auth0-spa';

type ContextProps = {
  loadingSummary: boolean;
  handleGetDashboardSummary: () => void;
  dashboardSummary: string;
  showPopup: boolean;
  setShowPopup: (value: boolean) => void; 
  setDashboardId: (value: string) => void;
  showSummarizeButton: boolean;
  setShowSummarizeButton: (value: boolean) => void;
  errorLoadingSummary: boolean;
  setDashboardFilters: (value: Record<string, string>) => void;
  handleResetSummary: () => void;
  setDisableSummarizeButton: (value: boolean) => void;
  disableSummarizeButton: boolean;
}

interface Props {}

export const DashboardSummaryContext = React.createContext<Partial<ContextProps>>({});
export const useDashboardSummary = () => useContext(DashboardSummaryContext);

export const DashboardSummaryProvider: React.FC<Props> = ({ children }) => {
  const { user, isClientUser, getTokenSilently } = useAuth0();
  const [showSummarizeButton, setShowSummarizeButton] = useState<boolean>(false);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [dashboardSummary, setDashboardSummary] = useState<string>('');
  const [dashboardFilters, setDashboardFilters] = useState<Record<string, string>>({});
  const [dashboardId, setDashboardId] = useState<string>('');
  const [errorLoadingSummary, setErrorLoadingSummary] = useState<boolean>(false);
  const [disableSummarizeButton, setDisableSummarizeButton] = useState<boolean>(true);
  const prefix = 'https://' + process.env.REACT_APP_AI_SUMMARY_API;

  const handleGetDashboardSummary = async () => {
    try {
      setLoadingSummary(true);
      setErrorLoadingSummary(false);
      const myToken = await getAuth0Token();
      if (!myToken) {
        throw new Error('Missing Auth0 access token');
      }
      const filtersData = getDashboardFilters();
      const requestBody = {
        filters_to_apply: filtersData.filters_to_apply,
        dashboard_id: dashboardId,
        global_filters: filtersData.global_filters
      };
      const response: AxiosResponse<{ summarization: string; s3_object: string; uuid: string }> = await axios.post(
        `${prefix}?token=${myToken}`,
        requestBody
      );
      setDashboardSummary(response.data.summarization);
    } catch (error) {
      setErrorLoadingSummary(true);
      setDashboardSummary('');
    } finally {
      setLoadingSummary(false);
    }
  }

  const getGlobalFilters = () => {
    return !isClientUser ? {
      'requisitions_transform.is_sevenstep': 'Yes'
    } : {};
  };

  const getDashboardFilters = () => {
    try {
      const lookerFilters = JSON.parse(localStorage.lookerFilters || null) || {};
      if (lookerFilters) {
        const filters = dashboardFilters || lookerFilters[dashboardId] || {};
        return {
          filters_to_apply: mapFilters(filters),
          global_filters: getGlobalFilters()
        };
      } else {
        return {
          filters_to_apply: {},
          global_filters: getGlobalFilters()
        };
      }
    } catch (error) {
      return {
        filters_to_apply: {},
        global_filters: getGlobalFilters()
      };
    }
  }

  const mapFilters = (filters: Record<string, string>) => {
    const filterKeys = Object.keys(filters);
    return filterKeys.reduce((prevValue: Record<string, string>, currentValue: string) => {
      if (filters.hasOwnProperty(currentValue) && filters[currentValue]) {
        prevValue[currentValue] = filters[currentValue];
      }
      return prevValue;
    }, {})
  }

  const handleResetSummary = () => {
    setShowPopup(false);
    setDashboardFilters({});
    setDashboardSummary('');
    setLoadingSummary(false);
    setErrorLoadingSummary(false);
  }

  // Token is issued by a Netlify function so AUTH0_API_SECRET never ships in the browser bundle.
  // Caller must present a valid Auth0 session token; forged user headers are rejected.
  const getAuth0Token = async () => {
    try {
      if (!getTokenSilently) {
        return '';
      }
      const sessionToken = await getTokenSilently();
      if (!sessionToken) {
        return '';
      }
      const options = {
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
          user: JSON.stringify(user),
        },
      };
      const response: AxiosResponse<{ access_token: string; token_type?: string }> = await axios.post(
        '/.netlify/functions/auth0Token',
        {},
        options,
      );
      if (!response.data.access_token) {
        return '';
      }
      const tokenType = response.data.token_type || 'Bearer';
      return `${tokenType} ${response.data.access_token}`;
    } catch (error) {
      return '';
    }
  };

  return (
    <DashboardSummaryContext.Provider
      value={{
        loadingSummary,
        handleGetDashboardSummary,
        dashboardSummary,
        showPopup,
        setShowPopup,
        setDashboardId,
        showSummarizeButton,
        setShowSummarizeButton,
        errorLoadingSummary,
        setDashboardFilters,
        handleResetSummary,
        setDisableSummarizeButton,
        disableSummarizeButton,
      }}
    >
      {children}
    </DashboardSummaryContext.Provider>
  )
}